import { Grid } from '@mui/material';
import { Text, Checkbox, OutlinedTextInput } from 'library';
import { useJobFormContext } from 'parent-portal/forms/JobFormContext';
import { DayOfWeekSelections, JobFormFields } from 'parent-portal/forms/JobRequestTypes';
import React, { useState } from 'react';
import FormField from '../../FormField';
import { FieldStates } from '../../FormTypes';
import JobFormPage from '../JobFormPage';
import SlotsPicker from './SlotsPicker';
import _ from 'lodash';

export default function ShiftDetails({ fieldStates }: { fieldStates: FieldStates<JobFormFields> }) {
    const { goToNext } = useJobFormContext();
    const oneDayJob =
        fieldStates.startDate.value &&
        fieldStates.endDate.value &&
        fieldStates.startDate.value.isSame(fieldStates.endDate.value);
    const { value: breakRequired, setValue: setBreakRequired } = fieldStates.breakRequired;
    const { value: breakLength, setValue: setBreakLength } = fieldStates.breakLength;
    const { value: daysOfTheWeek, setValue: setDaysOfTheWeek } = fieldStates.daysOfTheWeek;
    const selectedDays = Object.entries(daysOfTheWeek).filter((day) => day[1].selected);
    const [sameSlots, setSameSlots] = useState(
        oneDayJob || selectedDays.every((day) => day[1].slots === selectedDays[0][1].slots),
    );
    const { value: slotsAvailable, setValue: setSlotsAvailable } = fieldStates.slotsAvailable;
    function nextDisabled() {
        return breakRequired && !breakLength;
    }

    function updateAllSlots(slots: number) {
        setSlotsAvailable(slots);
        const updatedSlots = Object.entries(daysOfTheWeek).map(([day, values]) => [day, { ...values, slots }]);
        setDaysOfTheWeek(_.fromPairs(updatedSlots) as DayOfWeekSelections);
    }

    return (
        <JobFormPage
            onNextClicked={goToNext}
            title="Tell us about the shifts for this job."
            nextDisabled={nextDisabled()}
            id="shift-details-page"
        >
            <FormField title="Does this job require a break?" style={{ marginTop: 20 }}>
                <Checkbox
                    label="Break Required"
                    checked={breakRequired}
                    onChange={() => setBreakRequired(!breakRequired)}
                />
                {breakRequired ? (
                    <Grid container item direction="column" style={{ marginTop: 10 }}>
                        <Text>How long does the break need to be?</Text>
                        <Grid container item direction="row" alignItems="center" style={{ marginTop: 10, gap: 10 }}>
                            <OutlinedTextInput
                                type="number"
                                value={breakLength}
                                onChange={(e) => setBreakLength(parseInt(e.target.value))}
                                InputLabelProps={{ shrink: true }}
                                id="job-form-break-length-input"
                            />
                            <Text>Minutes</Text>
                        </Grid>
                    </Grid>
                ) : null}
            </FormField>
            <FormField title="How many workers would you like for each day of this job?" style={{ marginTop: 20 }}>
                <Grid container item direction="column" style={{ marginTop: 10 }}>
                    <Text>
                        If you need multiple workers per shift, you can add them here, no need to create a job for each
                        worker if the details are the same.{' '}
                    </Text>
                    <Grid container item direction="row" alignItems="center" style={{ marginTop: 10, gap: 10 }}>
                        <Grid container item direction="column">
                            {sameSlots && (
                                <SlotsPicker
                                    slotsAvailable={slotsAvailable}
                                    setSlotsAvailable={updateAllSlots}
                                    label="Number of workers"
                                />
                            )}
                            {!oneDayJob && (
                                <Checkbox
                                    label="Same number of workers for every day"
                                    checked={sameSlots}
                                    onChange={() => {
                                        if (!sameSlots) updateAllSlots(selectedDays[0][1].slots);
                                        setSameSlots(!sameSlots);
                                    }}
                                    style={{ marginBottom: 10 }}
                                />
                            )}
                            {!sameSlots && (
                                <Grid container direction="column" gap={1}>
                                    {Object.entries(daysOfTheWeek)
                                        .filter(([, values]) => values.selected)
                                        .map(([day, values]) => (
                                            <SlotsPicker
                                                key={day}
                                                slotsAvailable={values.slots}
                                                setSlotsAvailable={(slots) =>
                                                    setDaysOfTheWeek({ ...daysOfTheWeek, [day]: { ...values, slots } })
                                                }
                                                label={day}
                                            />
                                        ))}
                                </Grid>
                            )}
                        </Grid>
                    </Grid>
                </Grid>
            </FormField>
        </JobFormPage>
    );
}
